import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatApiError = (error) => {
  const detail = error.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e.msg || JSON.stringify(e)).join(', ');
  if (typeof detail === 'object' && detail !== null) return detail.msg || JSON.stringify(detail);
  return error.message || 'Error desconocido';
};

export function UserDialog({ open, onClose, user }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'consultora',
    color: '',
    pavilion_colors: {},
  });
  const [roles, setRoles] = useState([]);
  const [pavilions, setPavilions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        password: '',
        role: user.role || 'consultora',
        color: user.color || '',
        pavilion_colors: user.pavilion_colors || {},
      });
    }
  }, [user]);

  useEffect(() => {
    if (open) {
      axios.get(`${API_URL}/api/roles`, { withCredentials: true })
        .then(({ data }) => setRoles(data.filter(r => r.is_active !== false)))
        .catch((e) => console.error('Error loading roles:', e));
      axios.get(`${API_URL}/api/pavilions`, { withCredentials: true })
        .then(({ data }) => setPavilions(data.filter(p => p.is_active !== false)))
        .catch((e) => console.error('Error loading pavilions:', e));
    }
  }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (user) {
        // Modo edición
        const updateData = {
          name: formData.name,
          email: formData.email,
          role: formData.role,
          color: formData.color,
          pavilion_colors: formData.pavilion_colors || {},
        };
        
        // Solo incluir contraseña si se ingresó una nueva
        if (formData.password) {
          updateData.password = formData.password;
        }

        await axios.put(`${API_URL}/api/users/${user.id}`, updateData, {
          withCredentials: true,
        });
      } else {
        // Modo creación
        await axios.post(`${API_URL}/api/users`, formData, {
          withCredentials: true,
        });
      }
      onClose();
    } catch (error) {
      setError(formatApiError(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#FFFFFF] border-[#E8E5DD]">
        <DialogHeader>
          <DialogTitle className="text-2xl text-[#1A1C18]">
            {user ? 'Editar Usuario' : 'Nuevo Usuario'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="name">Nombre completo</Label>
            <Input
              id="name"
              data-testid="user-name-input"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              className="mt-1 bg-transparent border-[#E8E5DD]"
            />
          </div>

          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              data-testid="user-email-input"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              className="mt-1 bg-transparent border-[#E8E5DD]"
            />
          </div>

          <div>
            <Label htmlFor="password">
              {user ? 'Nueva contraseña (dejar vacío para mantener actual)' : 'Contraseña'}
            </Label>
            <Input
              id="password"
              type="password"
              data-testid="user-password-input"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required={!user}
              className="mt-1 bg-transparent border-[#E8E5DD]"
              placeholder={user ? 'Dejar vacío para no cambiar' : ''}
            />
          </div>

          <div>
            <Label htmlFor="role">Rol</Label>
            <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
              <SelectTrigger data-testid="user-role-select" className="mt-1 bg-transparent border-[#E8E5DD]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {roles.map((r) => (
                  <SelectItem key={r.key} value={r.key} data-testid={`user-role-option-${r.key}`}>
                    {r.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Color picker - shown for doctors */}
          {formData.role === 'doctor' && (
            <>
              <div>
                <Label>Color principal en calendario</Label>
                <div className="flex items-center gap-3 mt-1">
                  <input
                    type="color"
                    data-testid="user-color-input"
                    value={formData.color || '#8B5CF6'}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    className="w-10 h-10 rounded cursor-pointer border border-[#E8E5DD]"
                  />
                  <div className="flex gap-1.5 flex-wrap">
                    {['#D93025', '#E67C73', '#F4511E', '#F6BF26', '#33B679', '#0B8043', '#039BE5', '#4285F4', '#7986CB', '#8E24AA', '#616161'].map(c => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => setFormData({ ...formData, color: c })}
                        className={`w-7 h-7 rounded-full border-2 transition-transform hover:scale-110 ${formData.color === c ? 'border-[#2B4C3B] scale-110' : 'border-transparent'}`}
                        style={{ backgroundColor: c }}
                      />
                    ))}
                  </div>
                </div>
                <p className="text-xs text-[#6A736B] mt-1">
                  Se usa cuando el doctor opera en un pabellón sin color específico definido.
                </p>
              </div>

              {pavilions.length > 0 && (
                <div className="border border-[#E8E5DD] rounded-lg p-3 space-y-2 bg-[#F9F7F2]/50">
                  <Label className="text-sm font-medium">
                    Colores por pabellón (opcional)
                  </Label>
                  <p className="text-xs text-[#6A736B] -mt-1">
                    Ej: Dr. Terré usa un color en Estética y otro cuando opera en FMSC. Deja vacío para usar el color principal.
                  </p>
                  {pavilions.map((p) => {
                    const currentColor = formData.pavilion_colors?.[p.name] || '';
                    const setColor = (val) => setFormData({
                      ...formData,
                      pavilion_colors: { ...(formData.pavilion_colors || {}), [p.name]: val },
                    });
                    const clearColor = () => {
                      const copy = { ...(formData.pavilion_colors || {}) };
                      delete copy[p.name];
                      setFormData({ ...formData, pavilion_colors: copy });
                    };
                    return (
                      <div key={p.id} className="flex items-center gap-2" data-testid={`pav-color-row-${p.name}`}>
                        <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: p.color }} />
                        <span className="text-sm text-[#1A1C18] flex-1 truncate">{p.name}</span>
                        <input
                          type="color"
                          data-testid={`user-pav-color-${p.name}`}
                          value={currentColor || formData.color || '#8B5CF6'}
                          onChange={(e) => setColor(e.target.value)}
                          className="w-8 h-8 rounded cursor-pointer border border-[#E8E5DD]"
                        />
                        <Input
                          value={currentColor}
                          onChange={(e) => setColor(e.target.value)}
                          placeholder="—"
                          className="w-24 font-mono text-xs"
                        />
                        {currentColor && (
                          <button
                            type="button"
                            onClick={clearColor}
                            className="text-xs text-[#6A736B] hover:text-[#A65C5C] px-1"
                            title="Quitar color específico"
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          <div className="flex gap-3 justify-end">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              type="submit"
              data-testid="submit-user-button"
              disabled={loading}
              className="bg-[#2B4C3B] hover:bg-[#3A604D] text-white"
            >
              {loading ? (user ? 'Actualizando...' : 'Creando...') : (user ? 'Actualizar Usuario' : 'Crear Usuario')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
